import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Play, Gift, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FreeVideoOffer() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setIsSuccess(true);
      toast.success('¡Perfecto! Te contactaremos pronto.');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Este email ya está registrado');
      } else {
        toast.error('Error al registrar. Intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    'Sin compromiso de compra',
    'Conservas el video aunque no contrates',
    'Calidad profesional garantizada',
    'Entrega en 48-72 horas',
  ];

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 bg-gradient-to-b from-black via-[#0a0805] to-black overflow-hidden"
      data-testid="free-video-offer"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                <Gift size={24} className="text-[#D4AF37]" />
              </div>
              <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37]">
                Oferta Exclusiva
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6 leading-tight">
              Prueba Nuestra Calidad
              <br />
              <span className="gold-text">Sin Arriesgar Nada</span>
            </h2>

            <p className="text-lg text-zinc-400 leading-relaxed mb-6">
              Sabemos que elegir una agencia es una decisión importante. Por eso, 
              te ofrecemos un <span className="text-white font-semibold">video + edición completamente gratis</span> para 
              que evalúes nuestro trabajo antes de tomar cualquier decisión.
            </p>

            <p className="text-base text-zinc-500 leading-relaxed mb-8">
              Sin letra pequeña. Sin presiones. Si te gusta lo que ves, hablamos. 
              Si no, te quedas con un video profesional de regalo. Así de simple.
            </p>

            {/* Benefits */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-sm text-zinc-400">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Form Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/20 to-transparent blur-3xl opacity-30" />
            
            <div className="relative bg-zinc-950/80 border border-[#D4AF37]/20 p-8 md:p-10 backdrop-blur-sm">
              {!isSuccess ? (
                <>
                  <div className="flex items-center justify-center mb-8">
                    <motion.div
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ repeat: Infinity, duration: 3 }}
                      className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center"
                    >
                      <Play size={32} className="text-[#D4AF37] ml-1" fill="#D4AF37" fillOpacity={0.3} />
                    </motion.div>
                  </div>

                  <h3 className="text-2xl font-bold text-white text-center mb-2">
                    Video + Edición
                  </h3>
                  <p className="text-3xl font-bold text-[#D4AF37] text-center mb-6">
                    GRATIS
                  </p>

                  <p className="text-sm text-zinc-500 text-center mb-8">
                    Déjanos tu email y un miembro de nuestro equipo te contactará 
                    para coordinar tu video de prueba.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full bg-zinc-900 border border-white/10 focus:border-[#D4AF37] focus:outline-none px-4 py-4 text-white placeholder:text-zinc-600 transition-colors text-center"
                      data-testid="free-video-email"
                    />
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D4AF37] text-black py-4 font-bold tracking-wider uppercase text-sm hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="free-video-submit"
                    >
                      {isSubmitting ? 'Enviando...' : (
                        <>
                          Quiero Mi Video Gratis
                          <ArrowRight size={16} />
                        </>
                      )}
                    </motion.button>
                  </form>

                  <p className="text-xs text-zinc-600 text-center mt-6">
                    🔒 Tu información está segura. No spam, lo prometemos.
                  </p>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="w-20 h-20 mx-auto mb-6 bg-[#D4AF37]/20 rounded-full flex items-center justify-center"
                  >
                    <Sparkles size={36} className="text-[#D4AF37]" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-white mb-4">¡Excelente decisión!</h3>
                  <p className="text-zinc-400 mb-2">
                    Revisa tu bandeja de entrada.
                  </p>
                  <p className="text-sm text-zinc-500">
                    Nos pondremos en contacto en menos de 24 horas para coordinar tu video.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="absolute -bottom-4 -right-4 bg-[#D4AF37] text-black px-4 py-2 text-xs font-bold tracking-wider uppercase"
            >
              Sin Compromiso
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
